import { useTranslation } from 'react-i18next';
import {
  ArrowUpRight,
  BookOpen,
  Lightbulb,
  Link2,
  PlayCircle,
  Image as ImageIcon,
  ExternalLink,
  Sparkles,
} from 'lucide-react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { findHelpTopicById, type HelpCategory, type HelpTopic } from './help-center-topics';
import type { HelpTopicDetail } from './help-topic-detail.types';
import { HELP_TOPIC_DETAILS } from './help-topic-details.data';

const primaryRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:color-mix(in_srgb,var(--inst-player-primary,#001B3D)_45%,transparent)]';

function fallbackDetail(topic: HelpTopic): HelpTopicDetail {
  return {
    summary: topic.description,
    highlights: [
      { label: 'Fiche', value: topic.title },
      { label: 'Lyrra', value: 'Plateforme formation' },
      { label: 'Suite', value: 'À compléter' },
    ],
    steps: [
      {
        title: 'Ouvrir la fonctionnalité',
        description:
          'Utilisez le menu latéral ou le tableau de bord pour accéder au module concerné.',
      },
      {
        title: 'Suivre le flux métier',
        description:
          'Les écrans vous guident : enregistrez souvent et testez en prévisualisation lorsque c’est disponible.',
      },
      {
        title: 'Documenter en interne',
        description:
          'Notez les spécificités de votre établissement (process RH, nommage, charte) pour votre équipe.',
      },
    ],
    sections: [
      {
        heading: 'À retenir',
        paragraphs: [
          topic.description,
          'Le contenu détaillé de cette fiche sera enrichi ou relié à votre documentation interne.',
        ],
      },
    ],
    resources: [],
    quiz: [],
    tips: [
      'En cas de doute, vérifiez votre rôle (formateur, admin, RH) : les menus affichés en dépendent.',
    ],
    relatedTopicIds: [],
  };
}

export function resolveHelpTopicDetail(topic: HelpTopic): HelpTopicDetail {
  return HELP_TOPIC_DETAILS[topic.id] ?? fallbackDetail(topic);
}

interface HelpTopicModalProps {
  open: boolean;
  onClose: () => void;
  category: HelpCategory | null;
  topic: HelpTopic | null;
  onOpenTopic: (topicId: string) => void;
  /** Pour résoudre les sujets liés (même arborescence que la page d’aide). */
  lookupCategories?: readonly HelpCategory[] | null;
}

export function HelpTopicModal({
  open,
  onClose,
  category,
  topic,
  onOpenTopic,
  lookupCategories = null,
}: HelpTopicModalProps) {
  const { t } = useTranslation();
  if (!topic || !category) return null;

  const detail = resolveHelpTopicDetail(topic);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      ariaLabelledBy="help-topic-dialog-title"
      className="max-h-[min(90vh,calc(100dvh-2rem))] max-w-[min(56rem,calc(100vw-1.5rem))]"
    >
      <ModalHeader className="relative overflow-hidden border-0 bg-gradient-to-br from-[color:color-mix(in_srgb,var(--inst-player-primary,#001B3D)_08%,white)] to-white px-6 pb-5 pt-5 dark:from-[color:color-mix(in_srgb,var(--inst-player-primary,#001B3D)_12%,#111827)] dark:to-gray-900">
        <div
          className="pointer-events-none absolute -end-16 -top-16 h-48 w-48 rounded-full bg-[color:color-mix(in_srgb,var(--inst-player-primary,#001B3D)_18%,transparent)] blur-3xl dark:bg-[color:color-mix(in_srgb,var(--inst-player-primary,#001B3D)_25%,transparent)]"
          aria-hidden
        />
        <div className="relative flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:pe-10">
          <div className="flex min-w-0 flex-1 gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[color:color-mix(in_srgb,var(--inst-player-primary,#001B3D)_16%,transparent)] shadow-sm dark:bg-[color:color-mix(in_srgb,var(--inst-player-primary,#001B3D)_24%,transparent)]">
              <category.icon className="h-6 w-6 text-[color:var(--inst-player-primary,#001B3D)]" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-[color:var(--inst-player-primary,#001B3D)]">
                {category.title}
              </p>
              <h2
                id="help-topic-dialog-title"
                className="mt-0.5 text-xl font-bold leading-snug text-gray-900 dark:text-white"
              >
                {topic.title}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                {detail.summary}
              </p>
            </div>
          </div>
        </div>
      </ModalHeader>

      <ModalBody className="space-y-8 px-6 py-5">
        {detail.highlights.length > 0 && (
          <div className="grid gap-3 sm:grid-cols-3">
            {detail.highlights.map((h) => (
              <div
                key={`${h.label}-${h.value}`}
                className="rounded-xl border border-gray-100 bg-gray-50/90 px-4 py-3 dark:border-gray-700/80 dark:bg-gray-800/50"
              >
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {h.label}
                </p>
                <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-white">{h.value}</p>
              </div>
            ))}
          </div>
        )}

        {detail.steps.length > 0 && (
          <div>
            <div className="mb-4 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[color:var(--inst-player-primary,#001B3D)]" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                {t('help.modal.steps')}
              </h3>
            </div>
            <ol className="space-y-0">
              {detail.steps.map((step, i) => (
                <li key={i} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <span
                      className={cn(
                        'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white shadow-md',
                        'bg-[color:var(--inst-player-primary,#001B3D)]',
                      )}
                    >
                      {i + 1}
                    </span>
                    {i < detail.steps.length - 1 ? (
                      <span
                        className="mt-1 w-0.5 flex-1 min-h-[1.25rem] rounded-full bg-gradient-to-b from-[color:color-mix(in_srgb,var(--inst-player-primary,#001B3D)_55%,#e5e7eb)] to-gray-200 dark:from-[color:color-mix(in_srgb,var(--inst-player-primary,#001B3D)_50%,#374151)] dark:to-gray-700"
                        aria-hidden
                      />
                    ) : null}
                  </div>
                  <div className={cn('min-w-0 flex-1', i < detail.steps.length - 1 ? 'pb-6' : '')}>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{step.title}</p>
                    <p className="mt-1 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                      {step.description}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}

        <div className="space-y-6">
          {detail.sections.map((sec) => (
            <section key={sec.heading}>
              <div className="mb-2 flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{sec.heading}</h3>
              </div>
              <div className="space-y-3 rounded-xl border border-gray-100 bg-white p-4 dark:border-gray-700 dark:bg-gray-800/40">
                {sec.paragraphs.map((p, j) => (
                  <p key={j} className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                    {p}
                  </p>
                ))}
                {sec.bullets && sec.bullets.length > 0 ? (
                  <ul className="list-inside list-disc space-y-1.5 border-t border-gray-100 pt-3 text-sm text-gray-600 dark:border-gray-700 dark:text-gray-300">
                    {sec.bullets.map((b, k) => (
                      <li key={k}>{b}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </section>
          ))}
        </div>

        {detail.resources && detail.resources.length > 0 ? (
          <section>
            <div className="mb-2 flex items-center gap-2">
              <ExternalLink className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                {t('help.modal.resources')}
              </h3>
            </div>
            <ul className="space-y-2 rounded-xl border border-gray-100 bg-white p-4 text-sm dark:border-gray-700 dark:bg-gray-800/40">
              {detail.resources.map((resource, idx) => (
                <li key={`${resource.label}-${idx}`} className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                  {resource.type === 'video' ? (
                    <PlayCircle className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--inst-player-primary,#001B3D)]" />
                  ) : resource.type === 'image' ? (
                    <ImageIcon className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--inst-player-primary,#001B3D)]" />
                  ) : (
                    <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--inst-player-primary,#001B3D)]" />
                  )}
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white">{resource.label}</p>
                    <p className="font-mono text-xs text-gray-500 dark:text-gray-400">{resource.pathOrUrl}</p>
                    {resource.note ? <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">{resource.note}</p> : null}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {detail.quiz && detail.quiz.length > 0 ? (
          <section>
            <div className="mb-2 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[color:var(--inst-player-primary,#001B3D)]" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                {t('help.modal.quiz')}
              </h3>
            </div>
            <div className="space-y-3 rounded-xl border border-gray-100 bg-white p-4 dark:border-gray-700 dark:bg-gray-800/40">
              {detail.quiz.map((q, idx) => (
                <div key={`${q.question}-${idx}`} className="rounded-lg border border-gray-100 bg-gray-50/80 p-3 dark:border-gray-700 dark:bg-gray-800/60">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {idx + 1}. {q.question}
                  </p>
                  <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-gray-700 dark:text-gray-300">
                    {q.options.map((option, optionIndex) => (
                      <li key={optionIndex}>{option}</li>
                    ))}
                  </ul>
                  <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                    <strong>{t('help.modal.expectedAnswer')}</strong> {q.options[q.correctOptionIndex]}
                  </p>
                  <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">{q.explanation}</p>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {detail.tips.length > 0 ? (
          <div
            className={cn(
              'flex gap-3 rounded-xl border-s-4 p-4',
              'border-[color:var(--inst-player-primary,#001B3D)]',
              'bg-[color:color-mix(in_srgb,var(--inst-player-primary,#001B3D)_06%,#f9fafb)] dark:bg-[color:color-mix(in_srgb,var(--inst-player-primary,#001B3D)_10%,#1f2937)]',
            )}
          >
            <Lightbulb className="mt-0.5 h-5 w-5 shrink-0 text-[color:var(--inst-player-primary,#001B3D)]" />
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200">
                {t('help.modal.tips')}
              </p>
              <ul className="mt-2 space-y-2 text-sm text-gray-700 dark:text-gray-300">
                {detail.tips.map((tip, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-[color:var(--inst-player-primary,#001B3D)]" aria-hidden>
                      →
                    </span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}

        {detail.relatedTopicIds.length > 0 ? (
          <div>
            <div className="mb-2 flex items-center gap-2">
              <Link2 className="h-4 w-4 text-gray-400" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                {t('help.modal.related')}
              </h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {detail.relatedTopicIds.map((rid) => {
                const rel = findHelpTopicById(rid, lookupCategories);
                const label = rel?.topic.title ?? rid;
                return (
                  <button
                    key={rid}
                    type="button"
                    onClick={() => onOpenTopic(rid)}
                    className={cn(
                      'inline-flex max-w-full items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-start text-xs font-medium text-gray-700 transition-colors',
                      'hover:border-[color:color-mix(in_srgb,var(--inst-player-primary,#001B3D)_40%,#e5e7eb)] hover:bg-[color:color-mix(in_srgb,var(--inst-player-primary,#001B3D)_06%,white)]',
                      'dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-[color:color-mix(in_srgb,var(--inst-player-primary,#001B3D)_45%,#4b5563)]',
                      primaryRing,
                    )}
                  >
                    <span className="min-w-0 truncate">{label}</span>
                    <ArrowUpRight className="h-3 w-3 shrink-0 opacity-60" />
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </ModalBody>

      <ModalFooter className="bg-gray-50/80 dark:bg-gray-900/80">
        <Button type="button" variant="outline" onClick={onClose}>
          {t('btn.close')}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
